import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { Button } from "../../../components/button";
import { Loading } from "../../../components/loading";
import { useProduct } from "../../products/hooks";
import { useWarehouse } from "../../warehouses/hooks";
import { useCreateStockUnitFromScan } from "../hooks";
import { QrCodeScanner } from "../components";

const SUCCESS_DISPLAY_MS = 1000;

type IScanResult =
  | { kind: "success"; code: string }
  | { kind: "error"; code: string; message: string };

export const ScanToStock: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const productId = searchParams.get("productId");
  const warehouseId = searchParams.get("warehouseId");
  const batchNumber = searchParams.get("batchNumber");
  const quantity = searchParams.get("quantity");
  const enteredAt = searchParams.get("enteredAt");

  const contextReady =
    !!productId && !!warehouseId && !!batchNumber && !!quantity && !!enteredAt;

  const { data: productData, loading: productLoading } = useProduct(
    productId ? +productId : 0
  );
  const { data: warehouseData } = useWarehouse(
    warehouseId ? +warehouseId : 0
  );
  const { createFromScan } = useCreateStockUnitFromScan();

  const [scannedCount, setScannedCount] = useState(0);
  const [result, setResult] = useState<IScanResult | null>(null);
  const [busy, setBusy] = useState(false);

  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!contextReady) {
      navigate("/stock-unit/create");
    }
  }, [contextReady]);

  const handleScan = async (code: string) => {
    if (busy || result || !contextReady) return;

    setBusy(true);
    const response = await createFromScan({
      productId: +productId!,
      warehouseId: +warehouseId!,
      batchNumber: batchNumber!,
      quantity: +quantity!,
      enteredAt: enteredAt!,
      qrCode: code,
    });
    setBusy(false);

    if (response.success) {
      setScannedCount((c) => c + 1);
      setResult({ kind: "success", code });
      successTimerRef.current = setTimeout(() => {
        setResult(null);
      }, SUCCESS_DISPLAY_MS);
    } else {
      setResult({ kind: "error", code, message: response.error ?? "Erreur inconnue" });
    }
  };

  const dismissError = () => setResult(null);

  if (!contextReady || productLoading) return <Loading />;

  const product = productData?.product?.result;

  return (
    <>
      <Header
        title="Scanner les étiquettes"
        subtitle={product?.name}
        buttons={[
          {
            actionText: "Terminer",
            bgColor: "indigo",
            textColor: "white",
            link: productId ? `/product/${productId}` : "/products",
          },
        ]}
      />
      <div className="main-container">
        <div className="card mb-4 flex flex-wrap gap-6 text-sm">
          <div>
            <p className="text-gray-500">Produit</p>
            <p className="font-medium">{product?.name}</p>
          </div>
          <div>
            <p className="text-gray-500">Entrepôt</p>
            <p className="font-medium">{warehouseData?.warehouse?.result?.name}</p>
          </div>
          <div>
            <p className="text-gray-500">Numéro de lot</p>
            <p className="font-medium">{batchNumber}</p>
          </div>
          <div>
            <p className="text-gray-500">Quantité / unité</p>
            <p className="font-medium">{quantity}</p>
          </div>
          <div>
            <p className="text-gray-500">Date de réception</p>
            <p className="font-medium">{enteredAt}</p>
          </div>
        </div>

        <div className="card relative">
          <CardHeader
            title={`Scanner (${scannedCount} ajouté${scannedCount > 1 ? "s" : ""})`}
          />
          <QrCodeScanner active={!result} onScan={handleScan} />

          {result && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded">
              {result.kind === "success" ? (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl">
                  <p className="text-2xl mb-1">✅</p>
                  <p className="font-semibold text-gray-900">Produit ajouté</p>
                  <p className="text-xs text-gray-500 break-all mt-1">
                    {result.code}
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-lg px-6 py-5 text-center shadow-xl max-w-xs">
                  <p className="text-2xl mb-1">⚠️</p>
                  <p className="font-semibold text-red-600">Échec de la lecture</p>
                  <p className="text-sm text-gray-600 mt-1">{result.message}</p>
                  <p className="text-xs text-gray-400 break-all mt-2">
                    {result.code}
                  </p>
                  <Button className="mt-4" onClick={dismissError}>
                    Continuer le scan
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
